import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text,
} from 'react-native';
import Moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import Colors from '../../../constants/Colors';
import LocationSelector from '../LocationSelector';
import { updateBuildingId } from '../../../redux/selectedBuildingSlice';
import Style from '../../../constants/Style';
import MostUtilizedSpacesWidget from './components/MostUtilizedSpacesWidget';
import {
  Area, AreaType, Floor, Space,
} from '../../Map/SetFloorsModal/FloorItem';
import { DateRange, isLiveMode } from '../../../redux/dateRangeSlice';
// import getWidgetConfig from '../../../requests/Users/GetWidgetConfig';
import ClientUIConfig, { WidgetItem, WidgetSize, WidgetType } from '../../../widgetConfig';
import LineChartWidget from '../LineChartWidget';
import PieChartWidget from '../PieChartWidget';
import ScrollViewStyled from '../../ScrollViewStyled';
import Switch from '../../Switch';
import NextClean from '../ToiletWidgets/NextClean';
import MetricLineGraph from '../../../graphs/MetricLineGraph';
import TimesDashboard from '../TimesDashboard';
import WeatherDashboard from '../WeatherDashboard';
import PeaksWidget from '../PeaksWidget';
import TableOccupancyWidget from './components/TableOccupancyWidget';
import GetMetric, { AggregationFunction } from '../../../requests/Sensors/GetMetric';
import RoomBookings from './components/RoomBookings';
import { updateTicketsFilterLocation } from '../../../redux/ticketsFilterSlice';
import { LocationType, Metric, SensorLabel } from '../../../constants/Types';
import SensorsOffline from '../../Map/OccupancyWidgets/components/SensorsOffline';
import OccupancyDashboardHeader from './components/OccupancyDashboardHeader';
import OccupiedTimeChart from './components/OccupiedTimeChart';
import RoomUtilisationGraph from './components/RoomUtilisationGraph';
import BookingsGraph from './components/BookingsGraph';
import TimeComparisonGraph from './components/TimeComparisonGraph';
import { getRangeHours, mapBookingsListToBookedTimeData } from '../../../constants/Utils';
import LiveModeHeader from './components/LiveModeHeader';
import AverageOccupancyLineGraph from './components/AverageOccupancyLineGraph';
import FootfallGraph from './components/FootfallGraph';
import OccupancyBarGraph from './components/OccupancyBarGraph';
import RoomUtilizationGrid from './components/RoomUtilizationGrid';
import BookingsByTime from './components/BookingsByTime';
import {
  dataGroupedByDay, filterMetricData, getDayIndex, occupiedHours,
} from './Utils';
import { ChartDataPoint } from '../../../graphs/axisUtils';
import asNumber from '../../AsNumber';
import GetLocationMetadata from '../../../requests/Locations/GetLocationMetadata';
import GetAverageUtilization from '../../../requests/Sensors/Occupancy/GetAverageUtilization';
import getSpaces from '../../../requests/Locations/Spaces/GetSpaces';
import { AppScreen } from '../../../navigation/LinkingConfiguration';
import GetBookingsList from '../../../requests/Sensors/Occupancy/GetBookingsList';

type LocationOccupancyDashboardProps = {
  occupancyData: any,
  datePickerShown?: boolean,
  onConfirm,
  onCompareConfirm,
}

const LocationOccupancyDashboard = (props: LocationOccupancyDashboardProps) => {
  const route = useRoute();
  const navigation = useNavigation();
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => state.dateRange);
  const isSingleDay: boolean = getRangeHours(range) <= 24;
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => state.dateComparisonRange);
  const isSingleDayCompared: boolean = getRangeHours(compareRange) <= 24;
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>(
    useSelector((state: { selectedBuilding: { id: string } }) => state.selectedBuilding.id),
  );
  const buildings: object | null | undefined = useSelector((state:
    { locations }) => state.locations.buildings);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);
  const [locationHasSensors, setLocationHasSensors] = useState<boolean>(true);
  const [locationName, setLocationName] = useState<string>(
    useSelector((state: { selectedBuilding: { name: string } }) => state.selectedBuilding.name),
  );
  const [locationType, setLocationType] = useState<AreaType | null>(null);
  const [locationCapacity, setLocationCapacity] = useState<number | null>(null);
  const [locationsWithoutSensors, setLocationsWithoutSensors] = useState<boolean>(false);
  const [config, setConfig] = useState(null);
  const [maxOccupancyData, setMaxOccupancyData] = useState<ChartDataPoint[] | null>(null);
  const [maxOccupancyDataCompared,
    setMaxOccupancyDataCompared] = useState<ChartDataPoint[] | null>(null);
  const [hoursBookedData, setHoursBookedData] = useState<ChartDataPoint[] | null>(null);
  const [hoursBookedDataCompared,
    setHoursBookedDataCompared] = useState<ChartDataPoint[] | null>(null);
  const [hourlyOccupancyData, setHourlyOccupancyData] = useState<ChartDataPoint[] | null>(null);
  const [hourlyOccupancyDataCompared,
    setHourlyOccupancyDataCompared] = useState<ChartDataPoint[] | null>(null);
  const [granularOccupancyData, setGranularOccupancyData] = useState<ChartDataPoint[] | null>(null);
  const [granularOccupancyDataCompared,
    setGranularOccupancyDataCompared] = useState<ChartDataPoint[] | null>(null);
  const [utilisationData, setUtilisationData] = useState<ChartDataPoint[] | null>(null);
  const [utilisationDataCompared,
    setUtilisationDataCompared] = useState<ChartDataPoint[] | null>(null);
  const currentDay: number = getDayIndex(new Date(range.end));
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(currentDay ?? 0);
  const occupancyDataForDay:
    ChartDataPoint[] | null = dataGroupedByDay(hourlyOccupancyData, selectedDayIndex);
  const timeSpentData:
    ChartDataPoint[] | null = occupiedHours(granularOccupancyData, isSingleDay);
  const timeSpentDataCompared: ChartDataPoint[]
    | null = occupiedHours(granularOccupancyDataCompared, isSingleDayCompared);
  const [sublocationQueueData, setSublocationQueueData] = useState<ChartDataPoint[]>([]);
  const [mostUtilizedBuildingSublocations,
    setMostUtilizedBuildingSublocations] = useState<any[]>([]);
  const [mostUtilizedBuildingSublocationsCompared,
    setMostUtilizedBuildingSublocationsCompared] = useState<any[]>([]);
  const [mostUtilizedFloorSublocations, setMostUtilizedFloorSublocations] = useState<any[]>([]);
  const [mostUtilizedFloorSublocationsCompared,
    setMostUtilizedFloorSublocationsCompared] = useState<any[]>([]);
  const selectedAreaSublocationSpaces = buildings[selectedBuildingId]?.floors[selectedFloorId]
    ?.areas[selectedAreaId]?.spaces ?? [];
  const selectedLocationId: string = selectedSpaceId ?? selectedAreaId ?? selectedFloorId
    ?? selectedBuildingId;
  const subLocationQueueDataEnabled: boolean = false;

  useEffect(() => {
    const buildingId: string = (route?.params as any)?.buildingId;
    if (buildingId) {
      setSelectedBuildingId(buildingId);
      navigation.setParams({ buildingId: undefined } as any);
    }
    const floorId: string = (route?.params as any)?.floorId;
    if (floorId) {
      setSelectedFloorId(floorId);
      navigation.setParams({ floorId: undefined } as any);
    }
    const areaId: string = (route?.params as any)?.areaId;
    if (areaId) {
      setSelectedAreaId(areaId);
      navigation.setParams({ areaId: undefined } as any);
    }
    const spaceId: string = (route?.params as any)?.spaceId;
    if (spaceId) {
      setSelectedSpaceId(spaceId);
      navigation.setParams({ spaceId: undefined } as any);
    }
  }, [route]);

  useEffect(() => {
    GetMetric({
      metric: Metric.OCCUPANCY,
      range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
      aggregationFunction: AggregationFunction.MAX,
      externalId: selectedExternalId,
      interval: range.selectedRange === 7 ? '30s' : undefined,
    }).then((res) => setMaxOccupancyData(filterMetricData(res)));

    if (selectedAreaId || selectedSpaceId) {
      GetMetric({
        metric: Metric.OCCUPANCY,
        range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
        aggregationFunction: AggregationFunction.MAX,
        interval: '1h',
        externalId: selectedExternalId,
      }).then((res) => {
        setHourlyOccupancyData(filterMetricData(res));
      });

      GetMetric({
        metric: Metric.OCCUPANCY,
        range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
        aggregationFunction: AggregationFunction.MAX,
        interval: isSingleDay ? '1m' : '1h',
        externalId: selectedExternalId,
      }).then((res) => {
        setGranularOccupancyData(filterMetricData(res));
      });

      setHoursBookedData(null);
      if (isSingleDay) {
        GetBookingsList({
          range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
          locationId: selectedLocationId,
        }).then((res) => setHoursBookedData(mapBookingsListToBookedTimeData(res)))
          .catch(() => setHoursBookedData(null));
      } else {
        GetMetric({
          metric: Metric.HOURS_BOOKED,
          range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
          aggregationFunction: AggregationFunction.SUM,
          interval: isSingleDay ? '1h' : '1d',
          externalId: selectedExternalId,
        }).then((res) => setHoursBookedData(filterMetricData(res)
          .map(({ index, value, date }: ChartDataPoint) => ({
            index, date, value: Math.round(value * (isSingleDay ? 60 : 1)),
          })))).catch(() => setHoursBookedData(null));
      }
      GetMetric({
        metric: Metric.UTILIZATION,
        range: { start: range.start, end: Moment(range.end).endOf('day').toISOString() },
        aggregationFunction: AggregationFunction.MAX,
        externalId: selectedExternalId,
      }).then((res) => setUtilisationData(filterMetricData(res)
        .map(({ index, value, date }: ChartDataPoint) => ({
          index, date, value: value * 100,
        }))));
    } else {
      setHoursBookedData([]);
      setGranularOccupancyData([]);
      setHourlyOccupancyData([]);
    }

    if (selectedFloorId && !selectedAreaId && !selectedSpaceId) {
      setMostUtilizedFloorSublocations(null);
      GetAverageUtilization({
        range,
        parentLocationId: selectedExternalId,
      }).then((res) => {
        if (!res || res?.isError || res?.errorText) {
          setMostUtilizedFloorSublocations([]);
          return;
        }
        const externalIds: string[] = Object.keys(res ?? {})
          .sort((prev: string, next: string) => prev.localeCompare(next))
          .sort((prev: string, next: string) => Math.round(100 * res[next])
            - Math.round(100 * res[prev]));
        if (!externalIds.length) {
          setMostUtilizedFloorSublocations([]);
        } else {
          GetLocationMetadata(externalIds).then((metadata) => {
            setMostUtilizedFloorSublocations(externalIds.map((externalId: string) => ({
              title: metadata?.filter((item) => item.externalId === externalId)[0]?.name
                ?? externalId,
              value: asNumber(res[externalId] * 100),
              onPress: onLocationTilePress(externalId),
            })));
          });
        }
      });
    }

    if (!(selectedFloorId || selectedAreaId || selectedSpaceId)) {
      const buildingExternalId: string | undefined = buildings[selectedBuildingId]?.ExternalId;
      if (!buildingExternalId) return;
      GetAverageUtilization({
        range,
        parentLocationId: buildingExternalId,
      }).then((res) => {
        if (!res || res?.isError || res?.errorText) return;
        const externalIds: string[] = Object.keys(res ?? {})
          .sort((prev: string, next: string) => prev.localeCompare(next))
          .sort((prev: string, next: string) => Math.round(100 * res[next])
            - Math.round(100 * res[prev]));
        if (!externalIds.length) return;
        GetLocationMetadata(externalIds).then((metadata) => {
          setMostUtilizedBuildingSublocations(externalIds.map((externalId: string) => ({
            title: metadata?.filter((item) => item.externalId === externalId)[0]?.name
              ?? externalId,
            value: asNumber(res[externalId] * 100),
            onPress: onLocationTilePress(externalId),
          })));
        });
      });
    }
  }, [range, selectedExternalId]);

  useEffect(() => {
    if (!(compareRange?.start && compareRange?.end)) {
      setMostUtilizedBuildingSublocationsCompared(null);
      setMostUtilizedFloorSublocationsCompared(null);
      setMaxOccupancyDataCompared(null);
      setHourlyOccupancyDataCompared(null);
      setHoursBookedDataCompared(null);
      setUtilisationDataCompared(null);
      setGranularOccupancyDataCompared(null);
      return;
    }

    GetMetric({
      metric: Metric.OCCUPANCY,
      range: {
        start: compareRange.start,
        end: Moment(compareRange.end).endOf('day').toISOString(),
      },
      aggregationFunction: AggregationFunction.MAX,
      externalId: selectedExternalId,
    }).then((res) => setMaxOccupancyDataCompared(filterMetricData(res)));

    if (selectedAreaId || selectedSpaceId) {
      GetMetric({
        metric: Metric.OCCUPANCY,
        range: {
          start: compareRange.start,
          end: Moment(compareRange.end).endOf('day').toISOString(),
        },
        aggregationFunction: AggregationFunction.MAX,
        interval: '1h',
        externalId: selectedExternalId,
      }).then((res) => {
        setHourlyOccupancyDataCompared(filterMetricData(res));
      });

      GetMetric({
        metric: Metric.UTILIZATION,
        range: {
          start: compareRange.start,
          end: Moment(compareRange.end).endOf('day').toISOString(),
        },
        aggregationFunction: AggregationFunction.MAX,
        externalId: selectedExternalId,
      }).then((res) => setUtilisationDataCompared(filterMetricData(res)
        .map(({ index, value, date }: ChartDataPoint) => ({
          index, date, value: value * 100,
        }))));

      if (isSingleDayCompared) {
        GetBookingsList({
          range: {
            start: compareRange.start,
            end: Moment(compareRange.end).endOf('day').toISOString(),
          },
          locationId: selectedLocationId,
        }).then((res) => setHoursBookedDataCompared(mapBookingsListToBookedTimeData(res)))
          .catch(() => setHoursBookedDataCompared(null));
      } else {
        GetMetric({
          metric: Metric.HOURS_BOOKED,
          range: {
            start: compareRange.start,
            end: Moment(compareRange.end).endOf('day').toISOString(),
          },
          aggregationFunction: AggregationFunction.SUM,
          interval: isSingleDayCompared ? '1h' : '1d',
          externalId: selectedExternalId,
        }).then((res) => setHoursBookedDataCompared(filterMetricData(res)
          .map(({ index, value, date }: ChartDataPoint) => ({
            index, date, value: Math.round(value * (isSingleDayCompared ? 60 : 1)),
          })))).catch(() => setHoursBookedDataCompared(null));
      }

      GetMetric({
        metric: Metric.OCCUPANCY,
        range: {
          start: compareRange.start,
          end: Moment(compareRange.end).endOf('day').toISOString(),
        },
        aggregationFunction: AggregationFunction.MAX,
        interval: isSingleDay ? '1m' : '1h',
        externalId: selectedExternalId,
      }).then((res) => {
        setGranularOccupancyDataCompared(filterMetricData(res));
      });
    } else {
      setHourlyOccupancyDataCompared([]);
      setUtilisationDataCompared([]);
      setHoursBookedDataCompared([]);
      setGranularOccupancyDataCompared([]);
    }

    if (selectedFloorId && !selectedAreaId && !selectedSpaceId) {
      setMostUtilizedFloorSublocationsCompared(null);
      GetAverageUtilization({
        range: compareRange,
        parentLocationId: selectedExternalId,
      }).then((res) => {
        if (!res || res?.isError || res?.errorText) {
          setMostUtilizedFloorSublocationsCompared([]);
          return;
        }
        const externalIds: string[] = Object.keys(res ?? {})
          .sort((prev: string, next: string) => prev.localeCompare(next))
          .sort((prev: string, next: string) => Math.round(100 * res[next])
            - Math.round(100 * res[prev]));
        if (!externalIds.length) {
          setMostUtilizedFloorSublocationsCompared([]);
        } else {
          GetLocationMetadata(externalIds).then((metadata) => {
            setMostUtilizedFloorSublocationsCompared(externalIds.map((externalId: string) => ({
              title: metadata?.filter((item) => item.externalId === externalId)[0]?.name
                ?? externalId,
              value: asNumber(res[externalId] * 100),
              onPress: onLocationTilePress(externalId),
            })));
          });
        }
      });
    }

    if (!(selectedFloorId || selectedAreaId || selectedSpaceId)) {
      const buildingExternalId: string | undefined = buildings[selectedBuildingId]?.ExternalId;
      if (!buildingExternalId) return;
      GetAverageUtilization({
        range: compareRange,
        parentLocationId: buildingExternalId,
      }).then((res) => {
        if (!res || res?.isError || res?.errorText) return;
        const externalIds: string[] = Object.keys(res ?? {})
          .sort((prev: string, next: string) => prev.localeCompare(next))
          .sort((prev: string, next: string) => Math.round(100 * res[next])
            - Math.round(100 * res[prev]));
        if (!externalIds.length) return;
        GetLocationMetadata(externalIds).then((metadata) => {
          setMostUtilizedBuildingSublocationsCompared(externalIds.map((externalId: string) => ({
            title: metadata?.filter((item) => item.externalId === externalId)[0]?.name
              ?? externalId,
            value: asNumber(res[externalId] * 100),
            onPress: onLocationTilePress(externalId),
          })));
        });
      });
    }
  }, [compareRange, selectedExternalId]);

  useEffect(() => {
    if (!subLocationQueueDataEnabled) return;
    if (selectedSpaceId || !selectedAreaId) {
      setSublocationQueueData([]);
      return;
    }
    getSpaces(selectedAreaId).then(async (res) => {
      if (!(res?.locations?.length > 0)) {
        setSublocationQueueData([]);
        return;
      }
      const queues: Space[] = res.locations.filter((location: Space) => location.LocationSubtype
        === AreaType.QUEUE);
      const queueData = await Promise.all(queues.map((queue: Space) => GetMetric({
        metric: Metric.OCCUPANCY,
        range,
        interval: '1h',
        aggregationFunction: AggregationFunction.MAX,
        externalId: queue.ExternalId,
      })));
      const combinedQueueData = (queueData ?? [])
        .reduce((acc, data) => {
          acc.push(...(data ?? []));
          return acc;
        }, []);
      setSublocationQueueData(
        combinedQueueData
          .map((item: { timestamp: string, value: number | null }, index: number) => ({
            index,
            date: item.timestamp,
            // value: item.value,
            value: item.value || 0,
          })),
      );
    });
  }, [selectedAreaId, selectedSpaceId]);

  useEffect(() => {
    // getWidgetConfig().then((response) => {
    //   if (response?.occupancyPage) setConfig(response?.occupancyPage);
    //   else setConfig(ClientUIConfig.occupancyPage);
    // });
    setConfig(ClientUIConfig.occupancyPage);
  }, []);

  const onLocationTilePress = (externalId: string): (() => void | null) => {
    const subLocation: Area | Space = getSublocationByExternalId(externalId);
    if (!subLocation) return null;
    return () => {
      if (subLocation.Type === LocationType.AREA) {
        setSelectedFloorId(subLocation.ParentId);
        setSelectedAreaId(subLocation.LocationId);
        setSelectedSpaceId(null);
      } else {
        setSelectedFloorId(subLocation.floorId);
        setSelectedSpaceId(subLocation.LocationId);
        setSelectedAreaId(subLocation.ParentId);
      }
      setSelectedExternalId(subLocation.ExternalId);
      setLocationName(subLocation.Name);
      setLocationType(subLocation.LocationSubtype);
      setLocationCapacity(subLocation.LocationCapacity);
    };
  };

  const getSublocationByExternalId = (externalId: string): Area | Space | null => {
    if (!selectedBuildingId) return null;
    let areas: Area[];
    if (selectedFloorId) {
      areas = Object.values(buildings[selectedBuildingId]?.floors[selectedFloorId]
        ?.areas ?? {});
    } else {
      const floors: Floor[] = Object.values(buildings[selectedBuildingId]?.floors ?? {});
      areas = floors.reduce((acc: Area[], floor: Floor) => {
        acc.push(...Object.values(floor?.areas ?? {}));
        return acc;
      }, []);
    }
    if (!areas) return null;
    const selectedArea: Area | undefined = areas
      ?.filter((area: Area) => area.ExternalId === externalId)[0];
    if (selectedArea) return selectedArea;
    const spaces: Space[] = areas.reduce((acc: Space[], area: Area) => {
      acc.push(...Object.values(area?.spaces ?? {}));
      return acc;
    }, []);
    const selectedSpace: Space | undefined = spaces
      ?.filter((space: Space) => space.ExternalId === externalId)[0];
    if (!selectedSpace) return null;
    const parentArea: Area | undefined = areas
      ?.filter((area: Area) => area.LocationId === selectedSpace.ParentId)[0];
    return { ...selectedSpace, floorId: parentArea?.ParentId };
  };

  const dispatch = useDispatch();

  const navigateToTickets: () => void = () => {
    dispatch(updateBuildingId(selectedBuildingId));
    dispatch(updateTicketsFilterLocation(selectedLocationId));
    navigation.navigate(AppScreen.TICKETS as any, { filterDateRange: range });
  };

  const navigateToMap: () => void = () => {
    dispatch(updateBuildingId(selectedBuildingId));
    const floorId: string = selectedFloorId;
    const areaId: string = selectedAreaId;
    const spaceId: string = selectedSpaceId;
    navigation.navigate(AppScreen.MAP as any, { floorId, areaId, spaceId });
  };

  const liveModeHeader = () => <LiveModeHeader
    count={maxOccupancyData ? maxOccupancyData[maxOccupancyData.length - 1]?.value : null}
    target={locationCapacity}
  />;

  const renderWidget = ({
    name, size, widgets, title,
  }: WidgetItem) => {
    switch (name) {
      case WidgetType.ROW:
        return <View style={Style.row}>
          {widgets.map((item: WidgetItem, index: number) => <>
            {index > 0 && <View style={styles.rowSeparator} />}
            {renderWidget(item)}
          </>)}
        </View>;
      case WidgetType.SPACE_UTILIZATION:
        // eslint-disable-next-line no-console
        console.log(size);
        return liveModeHeader();
      case WidgetType.MOST_UTILIZED_SPACES:
        return <MostUtilizedSpacesWidget
          {...{ size }}
          data={mostUtilizedBuildingSublocations}
          comparisonData={mostUtilizedBuildingSublocationsCompared}
        />;
      case WidgetType.ROOM_UTILIZATION: {
        return <RoomUtilizationGrid
          spaces={Object.values(selectedAreaSublocationSpaces ?? {})}
        />;
      }
      case WidgetType.TABLE_OCCUPANCY:
        return <TableOccupancyWidget />;
      case WidgetType.AVERAGE_OCCUPANCY_BAR_GRAPH:
        return <OccupancyBarGraph
          barGraphData={hourlyOccupancyData}
          capacity={locationCapacity}
        />;
      case WidgetType.AVERAGE_OCCUPANCY_LINE_GRAPH:
        return <AverageOccupancyLineGraph
          {...{ locationCapacity }}
          chartData={maxOccupancyData}
          comparisonData={maxOccupancyDataCompared}
          alternateData={subLocationQueueDataEnabled ? sublocationQueueData : null}
          title={locationType === AreaType.QUEUE ? 'Queue' : 'Occupancy'}
        />;
      case WidgetType.FOOTFALL_LINE_GRAPH:
        return <FootfallGraph
          lineGraphData={maxOccupancyData}
          alternateLineGraphData={subLocationQueueDataEnabled ? sublocationQueueData : null}
        />;
      case WidgetType.AVERAGE_OCCUPANCY_LINE_GRAPH_WITH_COUNT:
        return <AverageOccupancyLineGraph
          {...{ locationCapacity }}
          chartData={maxOccupancyData}
          comparisonData={maxOccupancyDataCompared}
          alternateData={subLocationQueueDataEnabled ? sublocationQueueData : null}
          title={locationType === AreaType.QUEUE ? 'Queue' : 'Occupancy'}
          withCount
        />;
      case WidgetType.AVERAGE_OCCUPANCY_BAR_GRAPH_WITH_DAY_PICKER:
        return <OccupancyBarGraph
          {...{ selectedDayIndex, setSelectedDayIndex }}
          showCustomDateValue
          barGraphData={occupancyDataForDay}
          capacity={locationCapacity}
        />;
      case WidgetType.ROOM_BOOKINGS:
        return <RoomBookings locationId={selectedFloorId ?? selectedBuildingId} />;
      case WidgetType.BOOKINGS_BY_TIME:
        return <BookingsByTime
          {...{ title }}
          locationId={selectedLocationId}
        />;
      case WidgetType.LINE_GRAPH:
        return <LineChartWidget {...{ locationName, range, compareRange }} />;
      case WidgetType.PIE_CHART:
        return <PieChartWidget {...{ locationName, range, compareRange }} />;
      case WidgetType.PEAKS:
        return <PeaksWidget
          data={props.occupancyData}
          liveEnabled={range.selectedRange === 7}
          loading={false} />;
      case WidgetType.TIMES:
        return <TimesDashboard />;
      case WidgetType.WEATHER:
        return <WeatherDashboard />;
      case WidgetType.METRIC_LINE_GRAPH:
        return <MetricLineGraph
          title=''
          metric={Metric.OCCUPANCY}
          {...{ range, compareRange }}
        />;
      case WidgetType.NEXT_CLEAN:
        return <NextClean />;
      case WidgetType.OCCUPIED_TIME:
        return <OccupiedTimeChart
          data={hourlyOccupancyData}
          comparisonData={hourlyOccupancyDataCompared}
        />;
      case WidgetType.AVERAGE_UTILIZATION_GRAPH:
        return <RoomUtilisationGraph
          chartData={utilisationData}
          comparisonData={utilisationDataCompared}
        />;
      case WidgetType.BOOKINGS_GRAPH:
        return <BookingsGraph
          chartData={hoursBookedData}
          comparisonData={hoursBookedDataCompared}
        />;
      case WidgetType.TIME_COMPARISON_GRAPH:
        return <TimeComparisonGraph
          {...{
            hoursBookedData,
            timeSpentData,
            hoursBookedDataCompared,
            timeSpentDataCompared,
            selectedDayIndex,
            setSelectedDayIndex,
          }}
        />;
      default:
        return null;
    }
  };

  const renderWidgetsWithSeparation = (item: WidgetItem, isFirst: boolean = false) => <View
    style={[!isFirst && styles.widgetContainerWithMargin]}
  >
    {renderWidget(item)}
  </View>;

  return <View style={styles.container}>
    <View style={styles.locationSelectorSection}>
      <LocationSelector
        {...{
          setLocationName,
          setLocationType,
          setLocationCapacity,
          setSelectedExternalId,
          selectedBuildingId,
          setSelectedBuildingId,
          selectedFloorId,
          setSelectedFloorId,
          selectedAreaId,
          setSelectedAreaId,
          selectedSpaceId,
          setSelectedSpaceId,
          setLocationHasSensors,
          locationsWithoutSensors,
        }}
        sensorTypesShown={[SensorLabel.PCS]}
      />
      <View style={styles.includeSensorsSwitchSection}>
        <Text style={styles.includeSensorsLabel}>{'Show locations without sensors'}</Text>
        <Switch
          toggle={() => {
            setLocationsWithoutSensors(!locationsWithoutSensors);
          }}
          toggled={locationsWithoutSensors}
        />
      </View>
    </View>
    <View style={styles.content}>
      <OccupancyDashboardHeader
        {...{ navigateToMap, navigateToTickets, locationName }}
        datePickerShown={props.datePickerShown}
        onConfirm={props.onConfirm}
        onCompareConfirm={props.onCompareConfirm}
      />
      <ScrollViewStyled style={styles.scrollSection}>
        {(selectedBuildingId && !selectedFloorId && !selectedAreaId && !selectedSpaceId)
          && <>
            {config?.building
              && config?.building[isLiveMode(range) ? 'liveView' : 'historicView']
                .map((item: WidgetItem, index: number) => <View
                  style={{ zIndex: -1 * index }}
                  key={`building_view_widget_${index}`}
                >
                  {renderWidgetsWithSeparation(item, index === 0)}
                </View>)}
          </>}
        {selectedFloorId && locationHasSensors && !selectedAreaId && !selectedSpaceId
          && <MostUtilizedSpacesWidget
            data={mostUtilizedFloorSublocations}
            comparisonData={mostUtilizedFloorSublocationsCompared}
            size={WidgetSize.LARGE}
          />}
        {!!locationHasSensors && ((selectedAreaId || selectedSpaceId)
          && ((locationType && config?.area[locationType])
            ? <>
              {config?.area[locationType][isLiveMode(range) ? 'liveView' : 'historicView']
                .map((item: WidgetItem, index: number) => <View
                  style={{ zIndex: -1 * index }}
                  key={`widget_${index}`}
                >
                  {renderWidgetsWithSeparation(item, index === 0)}
                </View>)}
            </> : <>
              {isLiveMode(range) && liveModeHeader()}
              {[
                { name: WidgetType.AVERAGE_OCCUPANCY_BAR_GRAPH_WITH_DAY_PICKER },
                { name: WidgetType.AVERAGE_OCCUPANCY_LINE_GRAPH },
              ].map((item: WidgetItem, index: number) => <View
                style={{ zIndex: -1 * index }}
                key={`default_widget_${index}`}
              >
                {renderWidgetsWithSeparation(item, false)}
              </View>)}
            </>))}
        {!locationHasSensors && (selectedFloorId || selectedAreaId || selectedSpaceId)
          && <SensorsOffline
            title='No sensors in this location'
            style={styles.emptyState}
          />}
      </ScrollViewStyled>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#FFF',
    flexDirection: 'row',
    width: '100%',
    marginBottom: 16,
    height: 756,
  },
  locationSelectorSection: {
    borderEndWidth: 2,
    borderEndColor: Colors.light.hereworksWhite300,
    width: 240,
    zIndex: 2,
  },
  includeSensorsSwitchSection: {
    ...Style.row,
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    marginBottom: 12,
    alignItems: 'center',
  },
  includeSensorsLabel: {
    fontSize: 11,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Poppins_400Regular',
  },
  content: {
    flex: 3,
  },
  scrollSection: {
    paddingStart: 24,
    paddingEnd: 16,
    marginEnd: 8,
    paddingBottom: 24,
  },
  rowSeparator: {
    width: 16,
  },
  widgetContainerWithMargin: {
    marginTop: 16,
  },
  emptyState: {
    marginTop: '8%',
  },
});

export default LocationOccupancyDashboard;
